import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  CircularProgress,
  Alert,
  Container,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import axios from "axios";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import PaymentIcon from "@mui/icons-material/Payment";
import PrintIcon from "@mui/icons-material/Print";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery } from "@tanstack/react-query";
import ReceiptPrint from "./ReceiptPrint";

const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const TYPE_LABELS = {
  prescription: "Prescription",
  laboratory: "Laboratory",
  patient_xray: "X-Ray",
  ultrasound: "Ultrasound",
  patient_echo: "Echo",
  dental_treatment: "Dental",
  emergency: "Emergency",
  procedure: "Procedure",
  surgery: "Surgery",
  ipd_charge: "IPD",
  icu_charge: "ICU",
};

export default function CollectByTicket() {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [searchParams] = useSearchParams();
  const ticketIdFromUrl = searchParams.get("ticket");
  const ticketNumberFromUrl = searchParams.get("ticket_number");

  const [ticketInput, setTicketInput] = useState(ticketNumberFromUrl || ticketIdFromUrl || "");
  const [resolvedTicketId, setResolvedTicketId] = useState(ticketIdFromUrl ? Number(ticketIdFromUrl) : null);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [searchingTickets, setSearchingTickets] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => ({}));
  const [collecting, setCollecting] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();
  const searchDebounceRef = useRef(null);

  const headers = token ? { Authorization: `Token ${token}` } : {};

  const fetchTicketOptions = useCallback(
    async (query) => {
      const q = (query || ticketInput || "").trim();
      if (!q) {
        setTicketOptions([]);
        return;
      }
      setSearchingTickets(true);
      try {
        const url = `${baseUrl}/tickets/?search=${encodeURIComponent(q)}&page_size=20`;
        const res = await axios.get(url, { headers });
        const results = res.data?.results ?? res.data ?? [];
        const list = Array.isArray(results) ? results : [];
        setTicketOptions(list);
        if (list.length === 1) {
          setResolvedTicketId(list[0].id);
          setTicketInput(list[0].ticket_number || list[0].id.toString());
        } else if (list.length === 0) {
          toast.error(t("reception.ticketNotFound", "Ticket not found"));
        }
      } catch {
        setTicketOptions([]);
        toast.error(t("reception.loadError", "Failed to load tickets."));
      } finally {
        setSearchingTickets(false);
      }
    },
    [headers, t]
  );

  const handleTicketInputChange = (event, value, reason) => {
    setTicketInput(value || "");
    if (reason === "reset") return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const q = (value || "").trim();
    if (q.length < 1) {
      setTicketOptions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(() => fetchTicketOptions(q), 350);
  };

  const handleTicketOptionSelect = (event, option) => {
    if (option && option.id) {
      setResolvedTicketId(option.id);
      setTicketInput(option.ticket_number || option.id.toString());
      setTicketOptions([]);
    }
  };

  const duesQuery = useQuery({
    queryKey: ["ticket-dues", resolvedTicketId],
    queryFn: async () => {
      const res = await axios.get(`${baseUrl}/tickets/${resolvedTicketId}/dues/`, { headers });
      return res.data;
    },
    enabled: !!resolvedTicketId,
  });

  const ticket = duesQuery.data?.ticket;
  const items = duesQuery.data?.items ?? [];
  const unpaidItems = useMemo(() => items.filter((it) => !it.is_paid), [items]);
  const isLoadingDues = duesQuery.isLoading;
  const isErrorDues = duesQuery.isError;
  const duesError = duesQuery.error;

  const handleLoad = () => {
    const q = ticketInput.trim();
    if (!q) {
      toast.info(t("reception.enterTicketNumber", "Enter ticket number or ID"));
      return;
    }
    fetchTicketOptions(q);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const next = {};
      unpaidItems.forEach((it) => (next[`${it.type}-${it.id}`] = true));
      setSelectedIds(next);
    } else {
      setSelectedIds({});
    }
  };

  const handleSelectOne = (key, checked) => {
    setSelectedIds((prev) => ({ ...prev, [key]: checked }));
  };

  const selectedList = useMemo(
    () => unpaidItems.filter((it) => selectedIds[`${it.type}-${it.id}`]),
    [unpaidItems, selectedIds]
  );

  const collectOne = async (item) => {
    await collectItems([item]);
  };

  const collectItems = async (list) => {
    const toCollect = list.filter((it) => !it.is_paid);
    if (!toCollect.length) return;
    setCollecting(true);
    const errors = [];
    for (const item of toCollect) {
      try {
        await collectSingle(item);
      } catch (err) {
        errors.push({ item, err: err?.response?.data?.detail || err?.message });
      }
    }
    setCollecting(false);
    if (errors.length) {
      toast.error(
        t("reception.collectPartialError", "Some items could not be collected: ") +
          errors.map((e) => e.item.label).join(", ")
      );
    } else {
      toast.success(t("reception.collected", "Payment(s) recorded."));
    }
    duesQuery.refetch();
    setSelectedIds({});
  };

  const collectSingle = async (item) => {
    const { type, id, amount } = item;
    switch (type) {
      case "prescription":
        await axios.patch(`${baseUrl}/prescriptions/${id}/`, { payment_status: 2 }, { headers });
        break;
      case "laboratory":
        await axios.patch(`${baseUrl}/laboratory-test-orders/${id}/`, { is_paid: true }, { headers });
        break;
      case "patient_xray":
        await axios.patch(`${baseUrl}/patient-xray/${id}/`, { is_paid: true }, { headers });
        break;
      case "ultrasound":
        await axios.patch(`${baseUrl}/ultrasounds/${id}/`, { is_paid: true }, { headers });
        break;
      case "patient_echo":
        await axios.patch(`${baseUrl}/patient-echo/${id}/`, { is_paid: true }, { headers });
        break;
      case "dental_treatment":
        await axios.post(
          `${baseUrl}/dental-payments/`,
          { treatment: id, amount: amount || 0 },
          { headers: { ...headers, "Content-Type": "application/json" } }
        );
        break;
      case "emergency":
        await axios.post(`${baseUrl}/emergency-visits/${id}/mark-paid/`, {}, { headers });
        break;
      case "procedure":
        await axios.post(
          `${baseUrl}/procedure-payment/`,
          { procedure: id, amount: amount || 0 },
          { headers: { ...headers, "Content-Type": "application/json" } }
        );
        break;
      case "surgery":
        toast.info(t("reception.surgeryCollectNotSupported", "Surgery payment is not collected from this screen."));
        return;
      case "ipd_charge":
        await axios.post(`${baseUrl}/ipd-charges/${id}/mark-paid/`, {}, { headers });
        break;
      case "icu_charge":
        await axios.post(`${baseUrl}/icu-charges/${id}/mark-paid/`, {}, { headers });
        break;
      default:
        throw new Error(`Unknown type: ${type}`);
    }
  };

  const allUnpaidSelected = unpaidItems.length > 0 && unpaidItems.every((it) => selectedIds[`${it.type}-${it.id}`]);
  const someSelected = selectedList.length > 0;

  const handlePrintReceipt = (item) => {
    setPrintData({ ticket, item });
    setTimeout(() => {
      if (printRef.current) {
        const w = window.open("", "_blank");
        w.document.write(printRef.current.innerHTML);
        w.document.close();
        w.focus();
        w.print();
        w.close();
      }
    }, 300);
  };

  useEffect(() => {
    if (ticketIdFromUrl && /^\d+$/.test(ticketIdFromUrl)) {
      setResolvedTicketId(Number(ticketIdFromUrl));
    }
  }, [ticketIdFromUrl]);

  useEffect(() => {
    if (ticket && resolvedTicketId && ticket.ticket_number) {
      setTicketInput(ticket.ticket_number);
    }
  }, [resolvedTicketId, ticket?.ticket_number]);

  useEffect(() => {
    if (!ticketNumberFromUrl || resolvedTicketId != null) return;
    setTicketInput(ticketNumberFromUrl);
    const run = async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/tickets/?search=${encodeURIComponent(ticketNumberFromUrl)}`,
          { headers }
        );
        const results = res.data?.results ?? res.data ?? [];
        const first = Array.isArray(results) ? results[0] : results;
        if (first?.id) setResolvedTicketId(first.id);
      } catch (_) {}
    };
    run();
  }, [ticketNumberFromUrl]);

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("reception.paymentCollection", "Payment collection")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 320 }}
            inputValue={ticketInput}
            onInputChange={handleTicketInputChange}
            value={null}
            onChange={handleTicketOptionSelect}
            options={ticketOptions}
            getOptionLabel={(opt) => {
              const num = opt.ticket_number || opt.id?.toString() || "";
              const name = opt.patient_name ?? opt.patient_name_display ?? (opt.patient ? [opt.patient.firstname, opt.patient.lastname].filter(Boolean).join(" ") : null) ?? "";
              return name ? `${num} - ${name}` : num;
            }}
            loading={searchingTickets}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("reception.ticketNumberOrId", "Ticket number or ID")}
                placeholder={t("reception.ticketSearchPlaceholder", "e.g. 0001 or TKT-20260217-0001")}
                onKeyDown={(e) => e.key === "Enter" && (handleLoad(), e.preventDefault())}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchingTickets ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleLoad} disabled={searchingTickets}>
            {t("common.load", "Load")}
          </Button>
        </Stack>

        {isErrorDues && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {duesError?.response?.data?.detail || duesError?.message || t("reception.loadError", "Failed to load dues.")}
          </Alert>
        )}

        {resolvedTicketId && ticket && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Chip label={ticket.ticket_number} color="primary" />
              <Typography variant="body2">{ticket.patient_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {ticket.visit_date ? new Date(ticket.visit_date).toLocaleDateString() : ""}
              </Typography>
            </Stack>
          </Box>
        )}

        {isLoadingDues && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!resolvedTicketId && !isLoadingDues && (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            {t("reception.enterTicketToSeeDues", "Enter ticket number to see all payments.")}
          </Typography>
        )}

        {resolvedTicketId && !isLoadingDues && !isErrorDues && items.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            {t("reception.noChargeItems", "No charge items for this ticket.")}
          </Typography>
        )}

        {resolvedTicketId && !isLoadingDues && items.length > 0 && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={collecting ? <CircularProgress size={20} /> : <PaymentIcon />}
                disabled={!someSelected || collecting}
                onClick={() => collectItems(selectedList)}
              >
                {t("reception.collectSelected", "Collect selected")}
              </Button>
              <Button
                variant="outlined"
                disabled={collecting || unpaidItems.length === 0}
                onClick={() => {
                  if (window.confirm(t("reception.collectAllConfirm", "Collect payment for all unpaid items?"))) {
                    collectItems(unpaidItems);
                  }
                }}
              >
                {t("reception.collectAll", "Collect all")}
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {unpaidItems.length > 0 && (
                      <Checkbox
                        checked={allUnpaidSelected}
                        indeterminate={someSelected && !allUnpaidSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    )}
                  </TableCell>
                  <TableCell><strong>{t("reception.type", "Type")}</strong></TableCell>
                  <TableCell><strong>{t("reception.description", "Description")}</strong></TableCell>
                  <TableCell><strong>{t("reception.referredBy", "Referred by")}</strong></TableCell>
                  <TableCell><strong>{t("reception.date", "Date")}</strong></TableCell>
                  <TableCell align="right"><strong>{t("reception.amount", "Amount")}</strong></TableCell>
                  <TableCell align="center"><strong>{t("reception.status", "Status")}</strong></TableCell>
                  <TableCell align="center"><strong>{t("reception.action", "Actions")}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const key = `${item.type}-${item.id}`;
                  const isPaid = !!item.is_paid;
                  return (
                    <TableRow key={key} hover sx={{ bgcolor: isPaid ? "action.hover" : undefined }}>
                      <TableCell padding="checkbox">
                        {!isPaid && (
                          <Checkbox
                            checked={!!selectedIds[key]}
                            onChange={(e) => handleSelectOne(key, e.target.checked)}
                          />
                        )}
                      </TableCell>
                      <TableCell>{item.type_display || TYPE_LABELS[item.type] || item.type}</TableCell>
                      <TableCell>{item.label}</TableCell>
                      <TableCell>{item.doctor_display || "—"}</TableCell>
                      <TableCell>{item.date ? new Date(item.date).toLocaleDateString() : "—"}</TableCell>
                      <TableCell align="right">{item.amount != null ? `${item.amount} ؋` : "—"}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isPaid ? t("reception.paid") : t("reception.pending")}
                          color={isPaid ? "success" : "warning"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          <Tooltip title={t("reception.viewDetails", "View details")}>
                            <IconButton size="small" onClick={() => setDetailsItem(item)}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isPaid ? (
                            <Tooltip title={t("reception.printReceipt", "Print receipt")}>
                              <IconButton size="small" color="primary" onClick={() => handlePrintReceipt(item)}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={collecting}
                              onClick={() => collectOne(item)}
                            >
                              {t("reception.collect", "Collect")}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>

      {/* Details dialog */}
      <Dialog open={!!detailsItem} onClose={() => setDetailsItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("reception.itemDetails", "Item details")}</DialogTitle>
        <DialogContent>
          {detailsItem && (
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Box><strong>{t("reception.type", "Type")}:</strong> {detailsItem.type_display || TYPE_LABELS[detailsItem.type] || detailsItem.type}</Box>
              <Box><strong>{t("reception.description", "Description")}:</strong> {detailsItem.label}</Box>
              <Box><strong>{t("reception.referredBy", "Referred by")}:</strong> {detailsItem.doctor_display || "—"}</Box>
              <Box><strong>{t("reception.date", "Date")}:</strong> {detailsItem.date ? new Date(detailsItem.date).toLocaleString() : "—"}</Box>
              <Box><strong>{t("reception.amount", "Amount")}:</strong> {detailsItem.amount != null ? `${detailsItem.amount} ؋` : "—"}</Box>
              <Box>
                <strong>{t("reception.status", "Status")}:</strong>{" "}
                <Chip label={detailsItem.is_paid ? t("reception.paid") : t("reception.pending")} color={detailsItem.is_paid ? "success" : "warning"} size="small" />
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden receipt for printing */}
      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <ReceiptPrint data={printData} />
        </div>
      )}
    </Container>
  );
}
